import type { ProjectView } from '@/types/current-project';
import { projectStatus } from '@/lib/project-presentation';
import {
  useProjectBrief,
  useProjectParticipants,
} from '@/hooks/useProjectViews';
import { profileImageUrl } from '@/hooks/useProfile';
import { Avatar, Button } from '@/components/ui';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useProjectCommand } from '@/hooks/useProjectCommand';
import { useDaoConfig } from '@/hooks/useDaoConfig';
import { ProjectCommandFeedback } from './MilestoneCompletion';

export function ProjectHeader({
  project,
  onReviewMilestone,
}: {
  project: ProjectView;
  onReviewMilestone?: () => void;
}) {
  const { clientRole } = useDaoConfig();
  const navigate = useNavigate();
  const command = useProjectCommand(project.projectId);
  const brief = useProjectBrief(project.projectId).data?.brief;
  const participants = useProjectParticipants(project.projectId);
  const client = participants.data?.client;
  const profile = client?.profiles?.client;
  const account = useAuthStore((state) => state.session?.accountId);
  const pendingProposal = project.proposals.find(
    (p) => p.status === 'PendingApproval'
  );
  const pendingApproval = !!pendingProposal;
  const planningAccepted = ['Accepted', 'Delivered', 'Completed'].includes(
    project.planning.status
  );
  return (
    <div className="flex flex-col gap-10 lg:flex-row lg:items-center lg:justify-between">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-[--spacing-8]">
          <h1 className="text-3xl font-bold">{project.title}</h1>
          <span
            className="text-xs text-[--text-dark-tertiary]"
            title={project.projectId}
          >
            #{project.projectId.slice(2, 10)}
          </span>
        </div>
        {brief?.summary && (
          <p className="mt-2 text-base text-[--text-dark-secondary]">
            {brief.summary}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-[--spacing-5] text-left lg:shrink-0">
        {participants.isPending && (
          <p role="status">Loading {clientRole.toLowerCase()}…</p>
        )}
        {participants.isError && (
          <div role="alert">
            <p>Could not load {clientRole.toLowerCase()} details.</p>
            <Button
              variant="secondary"
              onClick={() => void participants.refetch()}
            >
              Retry participants
            </Button>
          </div>
        )}
        {client && (
          <>
            <div className="flex items-center gap-[--spacing-5]">
              <Avatar
                className="[&>div]:!h-[44px] [&>div]:!w-[44px]"
                size="lg"
                alt={profile?.name ?? client.displayName ?? clientRole}
                src={
                  client.clientImage && client.profiles
                    ? profileImageUrl(client.profiles.principalId, 'client')
                    : undefined
                }
              />
              <div>
                <p className="text-base font-medium">
                  {profile?.name ?? client.displayName ?? clientRole}
                </p>
                {profile?.company && (
                  <p className="text-sm text-[--text-dark-secondary]">
                    {profile.company}
                  </p>
                )}
              </div>
            </div>
            {(brief?.link || profile?.website) && (
              <a
                className="flex items-center gap-[--spacing-8] break-all text-base"
                href={brief?.link || profile?.website || undefined}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="ri-global-fill text-lg text-[--base-fill-5]" />
                {brief?.link || profile?.website}
              </a>
            )}
          </>
        )}
        <span className="sr-only">{projectStatus(project)}</span>
      </div>
      {account === project.client &&
        pendingApproval &&
        !project.cancelled &&
        !project.activeDisputeId && (
          <div className="flex flex-col items-end gap-[--spacing-5]">
            <span className="rounded-full border border-[--base-border] px-3 py-1 text-sm">
              Scope validation needed
            </span>
            <Button
              onClick={() =>
                navigate('/projects/' + project.projectId + '/review-scope')
              }
            >
              Review Milestones &amp; Cost{' '}
              <i aria-hidden="true" className="ri-arrow-right-line" />
            </Button>
          </div>
        )}
      {account === project.coordinator &&
        !project.cancelled &&
        !project.completed &&
        !project.planning.frozen &&
        !project.activeDisputeId &&
        !project.proposals.some((p) => p.status === 'Approved') && (
          <div className="flex flex-col items-end gap-[--spacing-5]">
            {pendingApproval && (
              <span className="rounded-full border border-[--base-border] px-3 py-1 text-sm">
                Waiting for {clientRole} approval
              </span>
            )}
            <Button
              variant={pendingApproval ? 'outline' : 'primary'}
              disabled={
                !planningAccepted ||
                command.busy ||
                command.pending ||
                command.blocked
              }
              title={
                pendingApproval
                  ? 'Return the submitted scope to draft without undoing payments.'
                  : planningAccepted
                    ? undefined
                    : `The ${clientRole.toLowerCase()} must accept the planning quote first.`
              }
              onClick={() => {
                if (!pendingProposal)
                  navigate('/projects/' + project.projectId + '/build-scope');
                else
                  void command
                    .run({
                      kind: 'proposal-withdraw',
                      projectId: project.projectId,
                      proposalId: pendingProposal.proposalId,
                      payload: { expectedRevision: pendingProposal.revision },
                    })
                    .then((result) => {
                      if (result)
                        navigate(
                          '/projects/' + project.projectId + '/build-scope'
                        );
                    });
              }}
            >
              {pendingApproval ? 'Rollback Submission' : 'Build Milestones'}
              {!pendingApproval && (
                <i className="ri-arrow-right-line text-2xl" />
              )}
            </Button>
            <ProjectCommandFeedback command={command} />
          </div>
        )}
      {onReviewMilestone &&
        account === project.coordinator &&
        !project.cancelled &&
        !project.completed &&
        !project.activeDisputeId && (
          <Button onClick={onReviewMilestone}>
            Review Milestone{' '}
            <i aria-hidden="true" className="ri-arrow-right-line" />
          </Button>
        )}
    </div>
  );
}
